import {
  addProtocol, GeoJSONSource, Map as MlMap, NavigationControl, setWorkerUrl, type StyleSpecification,
} from 'maplibre-gl'
// Same reason MapCanvas.tsx spells out: MapLibre 6 resolves its worker URL itself, which
// Vite neither rewrites nor emits -- without `?worker&url` the worker 404s and the map is
// a silent blank canvas. A Reports page may construct one of these maps with no other map
// ever having run on the page, so this module cannot lean on MapCanvas having set it.
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { Protocol } from 'pmtiles'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useFleet } from '../state/store'
import { buildRasterStyle, rasterBasemap, type BasemapMode } from './basemaps'
import { buildStyle } from './style'

/**
 * Shared plumbing for the small, self-owned MapLibre instances embedded in report
 * surfaces (`TripMap.tsx`, `LocationMap.tsx`) -- as opposed to `MapCanvas.tsx`, which is
 * a singleton bound to the live store and cannot be mounted twice or inside a drawer.
 *
 * Generalised from `busOps/NagpurTripReplayMap.tsx`: one `new Map`, a NavigationControl,
 * `remove()` on unmount, and a raster basemap by default. What that component got away
 * with and this one cannot is the `isStyleLoaded() ? draw() : once('load', draw)` guard
 * used bare: `isStyleLoaded()` also reports false while raster tiles are still streaming
 * in AFTER `load` has already fired, at which point a `once('load')` registered then
 * never fires and the draw is simply lost. A trip map gets a new playhead every scrubber
 * tick, so "lost draw" is not a corner case here -- `styleGate` below remembers that load
 * happened and queues by key until it has.
 */

let workerSet = false
/** Worker URL + pmtiles:// protocol, once per page -- same module-scoped guard idiom as
 *  MapCanvas.tsx / MbmtLiveMap.tsx / BusRouteMap.tsx (each registers its own; MapLibre's
 *  registry is a plain map keyed by scheme, so a repeat is harmless). */
export function ensureStandaloneWorker(): void {
  if (workerSet) return
  setWorkerUrl(workerUrl)
  addProtocol('pmtiles', new Protocol().tile)
  workerSet = true
}

/** A raster service replaces the whole style (basemaps.ts); anything else -- 'vector',
 *  or 'google2d' before its session exists -- falls through to the self-hosted archive via
 *  `buildStyle`, light theme (reports are white pages, the dark palette reads as a hole).
 *  `hasTiles`/`tilesVersion` are the store's own, populated by /api/geo at boot. */
export function standaloneStyle(mode: BasemapMode): StyleSpecification {
  const raster = rasterBasemap(mode)
  if (raster) return buildRasterStyle(raster)
  const { hasTiles, tilesVersion } = useFleet.getState()
  return buildStyle(hasTiles, tilesVersion, 'light', mode)
}

export function createStandaloneMap(
  container: HTMLElement, mode: BasemapMode, center: [number, number], zoom: number,
): MlMap {
  ensureStandaloneWorker()
  const map = new MlMap({
    container,
    style: standaloneStyle(mode),
    center,
    zoom,
    attributionControl: { compact: true },
    dragRotate: false,
    pitchWithRotate: false,
  })
  map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
  return map
}

export interface StyleGate {
  /** Run now if the style is ready, else once it is. Keyed so a burst of playhead updates
   *  before load collapses to the latest one instead of replaying every intermediate. */
  run(key: string, fn: () => void): void
  /** Call right after `map.setStyle(...)`: sources and layers are gone until 'style.load',
   *  so everything queues again until then. */
  rearm(): void
}

export function styleGate(map: MlMap): StyleGate {
  let ready = false
  const pending = new Map<string, () => void>()
  const flush = (): void => {
    ready = true
    const fns = [...pending.values()]
    pending.clear()
    for (const fn of fns) fn()
  }
  map.once('load', flush)
  return {
    run(key, fn) {
      if (ready || map.isStyleLoaded()) fn()
      else pending.set(key, fn)
    },
    rearm() {
      ready = false
      map.once('style.load', flush)
    },
  }
}

export type LngLat = [number, number]

export function isLngLat(lon: number | null | undefined, lat: number | null | undefined): boolean {
  return lon != null && lat != null && Number.isFinite(lon) && Number.isFinite(lat)
}

export function boundsOf(coords: readonly LngLat[]): [LngLat, LngLat] | null {
  const first = coords[0]
  if (!first) return null
  let minLon = first[0], maxLon = first[0], minLat = first[1], maxLat = first[1]
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }
  return [[minLon, minLat], [maxLon, maxLat]]
}

/** Camera over a set of points: a real box fits with padding, a single point (or a set
 *  of identical ones -- fitBounds on a zero-area box zooms to the max) jumps to it. */
export function fitTo(map: MlMap, coords: readonly LngLat[], padding = 40): void {
  const b = boundsOf(coords)
  if (!b) return
  const [[minLon, minLat], [maxLon, maxLat]] = b
  if (maxLon - minLon < 1e-6 && maxLat - minLat < 1e-6) {
    map.jumpTo({ center: [minLon, minLat], zoom: 14 })
    return
  }
  map.fitBounds(b, { padding, duration: 0, maxZoom: 16 })
}

export function setGeoJson(map: MlMap, srcId: string, data: GeoJSON.FeatureCollection | GeoJSON.Feature): void {
  const src = map.getSource(srcId)
  if (src instanceof GeoJSONSource) src.setData(data)
}

/** Popup bodies are built as HTML strings (MapLibre's Popup API); alert `message` is
 *  server text and must not be able to inject markup into the page. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
